import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    RoundingRuleCreate,
    RoundingRule,
    TypePunchOmitedRoundingEnum,
    TabsRoundingRules,
    RoundingRuleErrors,
    RoundingRuleFormData,
    RoundingRuleMutationPayload,
} from 'repository/timekeeper/@types/RoundingRules';
import { useRoundingRulesActions } from './useRoundingRulesActions';
import { normalizeString } from 'utils/Validations';
import dayjs, { Dayjs } from 'dayjs';
import { maxTime } from 'utils/roundingRules';

interface Props {
    rows: RoundingRule[];
    onClose: () => void;
    reloading: () => void;
    mode: 'add' | 'edit' | 'delete' | 'info';
    selectedRow: RoundingRule[];
    open: boolean;
}

const typePunchOmitedRoundingEnum: TypePunchOmitedRoundingEnum = {
    totals_real_projected: 'totals_real_projected',
    only_totals_projected: 'only_totals_projected',
};

const isValidTimeString = (value: string) => dayjs(value, ['HH:mm', 'HH:mm:ss'], true).isValid();

const normalizeTime = (value: string, fallback: string) => {
    if (!value) return fallback;
    if (!isValidTimeString(value)) return fallback;
    return dayjs(value, ['HH:mm', 'HH:mm:ss'], true).format('HH:mm');
};

const EMPTY_FORM: RoundingRuleCreate = {
    name: '',
    description: '',
    checkRoundToEmployeeSchedule: false,
    markingTolerance: 0,
    shiftTolerance: 0,
    config_early_punch_in: {
        in_out: true,
        early_late: true,
        change_point: '',
        rounding_extern: '',
        rounding_intern: '',
        grace_period_extern: '',
        grace_period_intern: '',
    },
    config_late_punch_in: {
        in_out: true,
        early_late: false,
        change_point: '',
        rounding_extern: '',
        rounding_intern: '',
        grace_period_extern: '',
        grace_period_intern: '',
    },
    config_early_punch_out: {
        in_out: false,
        early_late: true,
        change_point: '',
        rounding_extern: '',
        rounding_intern: '',
        grace_period_extern: '',
        grace_period_intern: '',
    },
    config_late_punch_out: {
        in_out: false,
        early_late: false,
        change_point: '',
        rounding_extern: '',
        rounding_intern: '',
        grace_period_extern: '',
        grace_period_intern: '',
    },
    config_unplan: {
        rounding_entry: '',
        grace_period_entry: '',
        rounding_exit: '',
        grace_period_exit: '',
        rounding_trans: '',
        grace_period_trans: '',
    },
    config_omited_punch: {
        planified: false,
        type: typePunchOmitedRoundingEnum.totals_real_projected,
        is_exception: false,
    },
};

const toFormFromRoundingRule = (roundingRule: RoundingRule): RoundingRuleCreate => {
    const punchConfigs = roundingRule.config_punch_r_rounding_rule ?? [];

    const getPunchConfig = (inOut: boolean, earlyLate: boolean) => {
        return punchConfigs.find((config) => config.in_out === inOut && config.early_late === earlyLate);
    };

    const configEarlyPunchIn = getPunchConfig(true, true);
    const configLatePunchIn = getPunchConfig(true, false);
    const configEarlyPunchOut = getPunchConfig(false, true);
    const configLatePunchOut = getPunchConfig(false, false);

    return {
        name: roundingRule.name ?? '',
        description: roundingRule.description ?? '',
        checkRoundToEmployeeSchedule: roundingRule.checkRoundToEmployeeSchedule ?? false,
        markingTolerance: roundingRule.markingTolerance ?? 0,
        shiftTolerance: roundingRule.shiftTolerance ?? 0,
        config_early_punch_in: {
            in_out: configEarlyPunchIn?.in_out ?? true,
            early_late: configEarlyPunchIn?.early_late ?? true,
            change_point: configEarlyPunchIn?.change_point ?? '',
            rounding_extern: configEarlyPunchIn?.rounding_extern ?? '',
            rounding_intern: configEarlyPunchIn?.rounding_intern ?? '',
            grace_period_extern: configEarlyPunchIn?.grace_period_extern ?? '',
            grace_period_intern: configEarlyPunchIn?.grace_period_intern ?? '',
        },
        config_late_punch_in: {
            in_out: configLatePunchIn?.in_out ?? true,
            early_late: configLatePunchIn?.early_late ?? false,
            change_point: configLatePunchIn?.change_point ?? '',
            rounding_extern: configLatePunchIn?.rounding_extern ?? '',
            rounding_intern: configLatePunchIn?.rounding_intern ?? '',
            grace_period_extern: configLatePunchIn?.grace_period_extern ?? '',
            grace_period_intern: configLatePunchIn?.grace_period_intern ?? '',
        },
        config_early_punch_out: {
            in_out: configEarlyPunchOut?.in_out ?? false,
            early_late: configEarlyPunchOut?.early_late ?? true,
            change_point: configEarlyPunchOut?.change_point ?? '',
            rounding_extern: configEarlyPunchOut?.rounding_extern ?? '',
            rounding_intern: configEarlyPunchOut?.rounding_intern ?? '',
            grace_period_extern: configEarlyPunchOut?.grace_period_extern ?? '',
            grace_period_intern: configEarlyPunchOut?.grace_period_intern ?? '',
        },
        config_late_punch_out: {
            in_out: configLatePunchOut?.in_out ?? false,
            early_late: configLatePunchOut?.early_late ?? false,
            change_point: configLatePunchOut?.change_point ?? '',
            rounding_extern: configLatePunchOut?.rounding_extern ?? '',
            rounding_intern: configLatePunchOut?.rounding_intern ?? '',
            grace_period_extern: configLatePunchOut?.grace_period_extern ?? '',
            grace_period_intern: configLatePunchOut?.grace_period_intern ?? '',
        },
        config_unplan: {
            rounding_entry: roundingRule.rounding_config_unplan_r_rounding_rule?.rounding_entry ?? '',
            grace_period_entry: roundingRule.rounding_config_unplan_r_rounding_rule?.grace_period_entry ?? '',
            rounding_exit: roundingRule.rounding_config_unplan_r_rounding_rule?.rounding_exit ?? '',
            grace_period_exit: roundingRule.rounding_config_unplan_r_rounding_rule?.grace_period_exit ?? '',
            rounding_trans: roundingRule.rounding_config_unplan_r_rounding_rule?.rounding_trans ?? '',
            grace_period_trans: roundingRule.rounding_config_unplan_r_rounding_rule?.grace_period_trans ?? '',
        },
        config_omited_punch: {
            planified: roundingRule.rounding_config_punch_omited_r_rounding_rule?.planified ?? false,
            type: roundingRule.rounding_config_punch_omited_r_rounding_rule?.type ?? typePunchOmitedRoundingEnum.only_totals_projected,
            is_exception: roundingRule.rounding_config_punch_omited_r_rounding_rule?.is_exception ?? false,
        },
    };
};

export const useRoundingRulesForm = ({ rows, onClose, reloading, mode, selectedRow, open }: Props) => {
    const currentRoundingRule = mode === 'edit' || mode === 'info' ? selectedRow[0] : undefined;
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { addRoundingRule, updateRoundingRule, deleteRoundingRule } = useRoundingRulesActions();
    const [initialForm, setInitialForm] = useState<RoundingRuleCreate>(EMPTY_FORM);
    const [form, setForm] = useState<RoundingRuleCreate>(EMPTY_FORM);
    const [alert, setAlert] = useState(false);
    const [value, setValue] = useState<keyof TabsRoundingRules>('entry');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;

        if ((mode === 'edit' || mode === 'info') && currentRoundingRule) {
            const preloadedForm = toFormFromRoundingRule(currentRoundingRule);
            setForm(preloadedForm);
            setInitialForm(preloadedForm);
            return;
        }
        setForm(EMPTY_FORM);
        setInitialForm(EMPTY_FORM);
    }, [open, mode, currentRoundingRule]);

    const update = <K extends keyof RoundingRuleCreate>(field: K, value: RoundingRuleCreate[K]) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const buildMutationPayload = (data: RoundingRuleCreate): RoundingRuleMutationPayload => {
        return {
            name: data.name,
            description: data.description,
            config_early_punch_in: {
                in_out: data.config_early_punch_in.in_out,
                early_late: data.config_early_punch_in.early_late,
                change_point: normalizeTime(data.config_early_punch_in.change_point, '00:00'),
                rounding_extern: normalizeTime(data.config_early_punch_in.rounding_extern, '00:01'),
                rounding_intern: normalizeTime(data.config_early_punch_in.rounding_intern, '00:01'),
                grace_period_extern: normalizeTime(data.config_early_punch_in.grace_period_extern, '00:00'),
                grace_period_intern: normalizeTime(data.config_early_punch_in.grace_period_intern, '00:00'),
            },
            config_late_punch_in: {
                in_out: data.config_late_punch_in.in_out,
                early_late: data.config_late_punch_in.early_late,
                change_point: normalizeTime(data.config_late_punch_in.change_point, '00:00'),
                rounding_extern: normalizeTime(data.config_late_punch_in.rounding_extern, '00:01'),
                rounding_intern: normalizeTime(data.config_late_punch_in.rounding_intern, '00:01'),
                grace_period_extern: normalizeTime(data.config_late_punch_in.grace_period_extern, '00:00'),
                grace_period_intern: normalizeTime(data.config_late_punch_in.grace_period_intern, '00:00'),
            },
            config_early_punch_out: {
                in_out: data.config_early_punch_out.in_out,
                early_late: data.config_early_punch_out.early_late,
                change_point: normalizeTime(data.config_early_punch_out.change_point, '00:00'),
                rounding_extern: normalizeTime(data.config_early_punch_out.rounding_extern, '00:01'),
                rounding_intern: normalizeTime(data.config_early_punch_out.rounding_intern, '00:01'),
                grace_period_extern: normalizeTime(data.config_early_punch_out.grace_period_extern, '00:00'),
                grace_period_intern: normalizeTime(data.config_early_punch_out.grace_period_intern, '00:00'),
            },
            config_late_punch_out: {
                in_out: data.config_late_punch_out.in_out,
                early_late: data.config_late_punch_out.early_late,
                change_point: normalizeTime(data.config_late_punch_out.change_point, '00:00'),
                rounding_extern: normalizeTime(data.config_late_punch_out.rounding_extern, '00:01'),
                rounding_intern: normalizeTime(data.config_late_punch_out.rounding_intern, '00:01'),
                grace_period_extern: normalizeTime(data.config_late_punch_out.grace_period_extern, '00:00'),
                grace_period_intern: normalizeTime(data.config_late_punch_out.grace_period_intern, '00:00'),
            },
            config_unplan: {
                rounding_entry: normalizeTime(data.config_unplan.rounding_entry, '00:01'),
                grace_period_entry: normalizeTime(data.config_unplan.grace_period_entry, '00:00'),
                rounding_exit: normalizeTime(data.config_unplan.rounding_exit, '00:01'),
                grace_period_exit: normalizeTime(data.config_unplan.grace_period_exit, '00:00'),
                rounding_trans: normalizeTime(data.config_unplan.rounding_trans, '00:01'),
                grace_period_trans: normalizeTime(data.config_unplan.grace_period_trans, '00:00'),
            },
            config_omited_punch: {
                planified: !!data.config_omited_punch.planified,
                type:
                    data.config_omited_punch.type === typePunchOmitedRoundingEnum.only_totals_projected
                        ? typePunchOmitedRoundingEnum.only_totals_projected
                        : typePunchOmitedRoundingEnum.totals_real_projected,
                is_exception: !!data.config_omited_punch.is_exception,
            },
        };
    };

    const handleSubmit = () => {
        if (mode === 'add') {
            addRoundingRule({
                setLoading: (value: boolean) => setLoading(value),
                body: buildMutationPayload(form),
                closing: onClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.rounding_rule.table.modals.add.messages.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Success',
                    message: t('settings.rounding_rule.table.modals.add.messages.success'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
        }

        if (mode === 'edit' && currentRoundingRule) {
            updateRoundingRule({
                setLoading: (value: boolean) => setLoading(value),
                roundingRuleId: currentRoundingRule.rounding_rule_id,
                body: buildMutationPayload(form),
                closing: onClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.rounding_rule.table.modals.edit.messages.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Success',
                    message: t('settings.rounding_rule.table.modals.edit.messages.success'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
        }

        if (mode === 'delete' && currentRoundingRule) {
            deleteRoundingRule({
                setLoading: (value: boolean) => setLoading(value),
                roundingRuleId: currentRoundingRule.rounding_rule_id,
                closing: onClose,
                reloading,
                messageError: {
                    title: 'Error',
                    message: t('settings.rounding_rule.table.modals.delete.messages.error'),
                    severity: 'error',
                },
                messageSuccess: {
                    title: 'Success',
                    message: t('settings.rounding_rule.table.modals.delete.messages.success'),
                    severity: 'success',
                },
                handlerAlert: (message) => {
                    enqueueSnackbar(message.message, { variant: message.severity });
                },
            });
        }
    };

    const existName = useMemo(() => {
        return (name: string) => {
            const normalizedInput = normalizeString(name);
            return rows.some((row) => {
                if (mode === 'edit' && currentRoundingRule && row.rounding_rule_id === currentRoundingRule.rounding_rule_id) return false;
                return normalizeString(row.name) === normalizedInput;
            });
        };
    }, [rows, mode, currentRoundingRule]);

    const cleanForm = () => {
        setForm(EMPTY_FORM);
        setInitialForm(EMPTY_FORM);
        setAlert(false);
        onClose();
        setValue('entry');
    };

    const disabled = () => {
        if (mode === 'info') return true;
        if (mode === 'edit') return JSON.stringify(form) === JSON.stringify(initialForm);
        return false;
    };

    const hasChanges = useMemo(() => {
        return JSON.stringify(form) !== JSON.stringify(initialForm);
    }, [form, initialForm]);

    const isValidTimeField = (momentValue: Dayjs | null | undefined): boolean => {
        return !momentValue || !dayjs(momentValue).isValid();
    };

    const [errors, setErrors] = useState<RoundingRuleErrors>({
        EntryEarlyExternalGracePeriod: '',
        EntryEarlyInternalGracePeriod: '',
        EntryLateExternalGracePeriod: '',
        EntryLateInternalGracePeriod: '',
        ExitEarlyExternalGracePeriod: '',
        ExitEarlyInternalGracePeriod: '',
        ExitLateExternalGracePeriod: '',
        ExitLateInternalGracePeriod: '',
        UnplannedEntryGracePeriod: '',
        UnplannedExitGracePeriod: '',
        UnplannedTransferGracePeriod: '',
    });

    const hasFormChanges = (data: RoundingRuleFormData): boolean => {
        const zero = dayjs(new Date().setHours(0, 0, 0, 0));
        const one = dayjs(new Date().setHours(0, 1, 0, 0));

        return (
            data.name !== '' ||
            data.description !== '' ||
            data.markingTolerance !== 0 ||
            data.shiftTolerance !== 0 ||
            !data.EntryEarlyChangePoint.isSame(zero) ||
            !data.EntryEarlyExternalRounding.isSame(one) ||
            !data.EntryEarlyInternalRounding.isSame(one) ||
            !data.EntryEarlyExternalGracePeriod.isSame(zero) ||
            !data.EntryEarlyInternalGracePeriod.isSame(zero) ||
            !data.EntryLateChangePoint.isSame(zero) ||
            !data.EntryLateExternalRounding.isSame(one) ||
            !data.EntryLateInternalRounding.isSame(one) ||
            !data.EntryLateExternalGracePeriod.isSame(zero) ||
            !data.EntryLateInternalGracePeriod.isSame(zero) ||
            !data.ExitEarlyChangePoint.isSame(zero) ||
            !data.ExitEarlyExternalRounding.isSame(one) ||
            !data.ExitEarlyInternalRounding.isSame(one) ||
            !data.ExitEarlyExternalGracePeriod.isSame(zero) ||
            !data.ExitEarlyInternalGracePeriod.isSame(zero) ||
            !data.ExitLateChangePoint.isSame(zero) ||
            !data.ExitLateExternalRounding.isSame(one) ||
            !data.ExitLateInternalRounding.isSame(one) ||
            !data.ExitLateExternalGracePeriod.isSame(zero) ||
            !data.ExitLateInternalGracePeriod.isSame(zero) ||
            !data.UnplannedEntryRounding.isSame(one) ||
            !data.UnplannedEntryGracePeriod.isSame(zero) ||
            !data.UnplannedExitRounding.isSame(one) ||
            !data.UnplannedExitGracePeriod.isSame(zero) ||
            !data.UnplannedTransferRounding.isSame(one) ||
            !data.UnplannedTransferGracePeriod.isSame(zero) ||
            !data.CheckUseScheduleTime ||
            data.RadioUseScheduleTime !== 'projected_and_real_totals' ||
            !data.CheckTreatAsAnException
        );
    };

    const isFormDisabled = (data: RoundingRuleFormData & { errors: RoundingRuleErrors }): boolean => {
        const e = data.errors;
        return (
            !data.name ||
            data.name === '' ||
            data.markingTolerance < 0 ||
            data.shiftTolerance < 0 ||
            isValidTimeField(data.EntryEarlyChangePoint) ||
            isValidTimeField(data.EntryEarlyExternalRounding) ||
            isValidTimeField(data.EntryEarlyInternalRounding) ||
            isValidTimeField(data.EntryEarlyExternalGracePeriod) ||
            isValidTimeField(data.EntryEarlyInternalGracePeriod) ||
            isValidTimeField(data.EntryLateChangePoint) ||
            isValidTimeField(data.EntryLateExternalRounding) ||
            isValidTimeField(data.EntryLateInternalRounding) ||
            isValidTimeField(data.EntryLateExternalGracePeriod) ||
            isValidTimeField(data.EntryLateInternalGracePeriod) ||
            isValidTimeField(data.ExitEarlyChangePoint) ||
            isValidTimeField(data.ExitEarlyExternalRounding) ||
            isValidTimeField(data.ExitEarlyInternalRounding) ||
            isValidTimeField(data.ExitEarlyExternalGracePeriod) ||
            isValidTimeField(data.ExitEarlyInternalGracePeriod) ||
            isValidTimeField(data.ExitLateChangePoint) ||
            isValidTimeField(data.ExitLateExternalRounding) ||
            isValidTimeField(data.ExitLateInternalRounding) ||
            isValidTimeField(data.ExitLateExternalGracePeriod) ||
            isValidTimeField(data.ExitLateInternalGracePeriod) ||
            isValidTimeField(data.UnplannedEntryRounding) ||
            isValidTimeField(data.UnplannedEntryGracePeriod) ||
            isValidTimeField(data.UnplannedExitRounding) ||
            isValidTimeField(data.UnplannedExitGracePeriod) ||
            isValidTimeField(data.UnplannedTransferRounding) ||
            isValidTimeField(data.UnplannedTransferGracePeriod) ||
            e.EntryEarlyExternalGracePeriod === 'maxTime' ||
            e.EntryEarlyInternalGracePeriod === 'maxTime' ||
            e.EntryLateExternalGracePeriod === 'maxTime' ||
            e.EntryLateInternalGracePeriod === 'maxTime' ||
            e.ExitEarlyExternalGracePeriod === 'maxTime' ||
            e.ExitEarlyInternalGracePeriod === 'maxTime' ||
            e.ExitLateExternalGracePeriod === 'maxTime' ||
            e.ExitLateInternalGracePeriod === 'maxTime' ||
            e.UnplannedEntryGracePeriod === 'maxTime' ||
            e.UnplannedExitGracePeriod === 'maxTime' ||
            e.UnplannedTransferGracePeriod === 'maxTime'
        );
    };

    const buildRoundingRuleBody = (data: RoundingRuleFormData): any => {
        const zero = dayjs(new Date().setHours(0, 0, 0, 0));

        const body: any = {
            name: data.name,
            description: data.description,
            config_early_punch_in: {
                in_out: true,
                early_late: true,
                change_point: data.EntryEarlyChangePoint.format('HH:mm'),
                rounding_extern: data.EntryEarlyExternalRounding.format('HH:mm'),
                rounding_intern: zero.isSame(data.EntryEarlyChangePoint) ? undefined : data.EntryEarlyInternalRounding.format('HH:mm'),
                grace_period_extern: data.EntryEarlyExternalGracePeriod.format('HH:mm'),
                grace_period_intern: zero.isSame(data.EntryEarlyChangePoint) ? undefined : data.EntryEarlyInternalGracePeriod.format('HH:mm'),
            },
            config_late_punch_in: {
                in_out: true,
                early_late: false,
                change_point: data.EntryLateChangePoint.format('HH:mm'),
                rounding_extern: data.EntryLateExternalRounding.format('HH:mm'),
                rounding_intern: zero.isSame(data.EntryLateChangePoint) ? undefined : data.EntryLateInternalRounding.format('HH:mm'),
                grace_period_extern: data.EntryLateExternalGracePeriod.format('HH:mm'),
                grace_period_intern: zero.isSame(data.EntryLateChangePoint) ? undefined : data.EntryLateInternalGracePeriod.format('HH:mm'),
            },
            config_early_punch_out: {
                in_out: false,
                early_late: true,
                change_point: data.ExitEarlyChangePoint.format('HH:mm'),
                rounding_extern: data.ExitEarlyExternalRounding.format('HH:mm'),
                rounding_intern: zero.isSame(data.ExitEarlyChangePoint) ? undefined : data.ExitEarlyInternalRounding.format('HH:mm'),
                grace_period_extern: data.ExitEarlyExternalGracePeriod.format('HH:mm'),
                grace_period_intern: zero.isSame(data.ExitEarlyChangePoint) ? undefined : data.ExitEarlyInternalGracePeriod.format('HH:mm'),
            },
            config_late_punch_out: {
                in_out: false,
                early_late: false,
                change_point: data.ExitLateChangePoint.format('HH:mm'),
                rounding_extern: data.ExitLateExternalRounding.format('HH:mm'),
                rounding_intern: zero.isSame(data.ExitLateChangePoint) ? undefined : data.ExitLateInternalRounding.format('HH:mm'),
                grace_period_extern: data.ExitLateExternalGracePeriod.format('HH:mm'),
                grace_period_intern: zero.isSame(data.ExitLateChangePoint) ? undefined : data.ExitLateInternalGracePeriod.format('HH:mm'),
            },
            config_unplan: {
                rounding_entry: data.UnplannedEntryRounding.format('HH:mm'),
                rounding_exit: data.UnplannedExitRounding.format('HH:mm'),
                rounding_trans: data.UnplannedTransferRounding.format('HH:mm'),
                grace_period_entry: data.UnplannedEntryGracePeriod.format('HH:mm'),
                grace_period_exit: data.UnplannedExitGracePeriod.format('HH:mm'),
                grace_period_trans: data.UnplannedTransferGracePeriod.format('HH:mm'),
            },
            config_omited_punch: {
                planified: data.CheckUseScheduleTime,
                type: data.CheckUseScheduleTime ? data.RadioUseScheduleTime : undefined,
                is_exception: data.CheckUseScheduleTime ? data.CheckTreatAsAnException : undefined,
            },
        };

        return body;
    };

    const detectChanges = () => {
        if (hasChanges) {
            setAlert(true);
        } else {
            cleanForm();
        }
    };

    return {
        form,
        alert,
        value,
        update,
        setAlert,
        setValue,
        disabled,
        cleanForm,
        existName,
        hasChanges,
        handleSubmit,
        hasFormChanges,
        isFormDisabled,
        buildRoundingRuleBody,
        errors,
        setErrors,
        loading,
        setLoading,
        maxTime,
        detectChanges,
    };
};
